import type { ChatTab, SavedAction, Space, UserRole } from '@/types';

export interface SidebarStaticHistory {
  id: string;
  title: string;
}

export const STATIC_CHAT_HISTORY_BY_ROLE: Record<UserRole, SidebarStaticHistory[]> = {
  operator: [
    { id: 'op-1', title: 'Badge batch week pending' },
    { id: 'op-2', title: 'Access denied spike main' },
  ],
  novice: [],
};

export const SPACES_BY_ROLE: Record<UserRole, Space[]> = {
  operator: [
    { id: '1', name: 'User credential issue' },
    { id: '2', name: 'Controller Ip change' },
  ],
  novice: [],
};

export const DEFAULT_SAVED_ACTIONS_BY_ROLE: Record<UserRole, SavedAction[]> = {
  operator: [
    { id: '1', template: 'Check why @user was denied at @device', isPinned: true },
    { id: '2', template: 'Add @user to @access-group for @schedule', isPinned: true },
    { id: '3', template: 'List active credentials for @user at this site', isPinned: true },
  ],
  novice: [],
};

export type QuickActionCard = {
  id: string;
  text: string;
  pinned?: boolean;
};

/** Top suggestion cards keyed by composer tab (Users, Devices, …) and role. */
export const QUICK_ACTIONS_BY_TAB_AND_ROLE: Record<UserRole, Record<ChatTab, QuickActionCard[]>> = {
  novice: {
    users: [
      { id: 'users-1', text: 'Create a new user with a card' },
      { id: 'users-2', text: 'Add a user to a first-floor access group' },
      { id: 'users-3', text: 'Check why a user was denied at a reader' },
      { id: 'users-4', text: 'List users without active credentials on this site' },
    ],
    devices: [
      { id: 'devices-1', text: 'Add a controller to this system' },
      { id: 'devices-2', text: 'Configure doors for a new reader' },
      { id: 'devices-3', text: 'Check controller connectivity for this site' },
      { id: 'devices-4', text: 'List devices and access points on this site' },
    ],
    credentials: [
      { id: 'credentials-1', text: 'Issue a new card for a user' },
      { id: 'credentials-2', text: 'Update card format or facility code for a user' },
      { id: 'credentials-3', text: 'Revoke and reissue a lost credential' },
      { id: 'credentials-4', text: 'List active credentials for a user at this site' },
    ],
    events: [
      { id: 'events-1', text: 'Review access events for this site' },
      { id: 'events-2', text: 'Find denied access events in the last 24 hours' },
      { id: 'events-3', text: 'Summarize unusual door activity this week' },
      { id: 'events-4', text: 'Export an access audit report for compliance' },
    ],
  },
  operator: {
    users: [
      { id: 'users-1', text: 'Start adding a new user for this site' },
      { id: 'users-2', text: "Change the user's access groups or schedule" },
      { id: 'users-3', text: 'Check why @user was denied at @device' },
      { id: 'users-4', text: 'Add the user to user group', pinned: true },
    ],
    devices: [
      { id: 'devices-1', text: 'Add a controller to this system' },
      { id: 'devices-2', text: 'Change controller IP or network settings' },
      { id: 'devices-3', text: 'List offline panels at this site' },
      { id: 'devices-4', text: 'Pair a new reader to an access point' },
    ],
    credentials: [
      { id: 'credentials-1', text: 'Update card format or facility code for the user' },
      { id: 'credentials-2', text: 'List active credentials for @user at this site' },
      { id: 'credentials-3', text: 'Bulk reissue badges for an access group' },
      { id: 'credentials-4', text: 'Deactivate a lost card for @user' },
    ],
    events: [
      { id: 'events-1', text: 'Review access events for this site' },
      { id: 'events-2', text: 'Investigate denied access spike on main entrance' },
      { id: 'events-3', text: 'Compare event volume week over week' },
      { id: 'events-4', text: 'Filter events by @device and time range' },
    ],
  },
};

export function getQuickActionsFor(role: UserRole, tab: ChatTab): QuickActionCard[] {
  return QUICK_ACTIONS_BY_TAB_AND_ROLE[role]?.[tab] ?? QUICK_ACTIONS_BY_TAB_AND_ROLE.novice.users;
}

/** @deprecated Use getQuickActionsFor(role, tab) — kept for tests or legacy imports. */
export const QUICK_ACTIONS_BY_ROLE: Record<UserRole, QuickActionCard[]> = {
  operator: QUICK_ACTIONS_BY_TAB_AND_ROLE.operator.users,
  novice: QUICK_ACTIONS_BY_TAB_AND_ROLE.novice.users,
};

export type OnboardingStep = {
  id: string;
  title: string;
  desc: string;
};

export const GET_STARTED_STEPS: OnboardingStep[] = [
  {
    id: 'device',
    title: 'Add a device',
    desc: 'Add a controller (panel), then configure doors / access points so access groups can include those doors.',
  },
  {
    id: 'user',
    title: 'Add a user with a credential',
    desc: 'Create a user in the system and assign them a credential, a card, PIN so they can authenticate at readers.',
  },
  {
    id: 'access',
    title: 'Create an access group',
    desc: 'Define a group with doors and a schedule so users can access the right areas.',
  },
];

export const WORKSPACE_FLOORS = ['3rd floor', '9th floor', '10th floor'] as const;

